import { Component, Input, OnChanges } from '@angular/core';
import { BASALT, BASALT_FACES, BASALT_FACES_ALT, BASALT_TOPS } from '../line-basalt-glyph';
import { Product } from '../../../model/product/product.model';
import { PEAK_BODY, PEAK_CAP, PEAK_CAP_SMALL } from '../line-peak-glyph';
import { ORB_BANDS, ORB_COLORS, ORB_R, ORB_RIM_OFF, ORB_RIM_STOPS, ORB_STARS, ORB_TAB_SET } from '../line-orb-glyph';
import { TREE_BARK, TREE_CANOPY, TREE_DARK, TREE_LIGHT, TREE_MID, TREE_TRUNK } from '../line-tree-glyph';
import { MAPLE_BLADE, MAPLE_DEEP, MAPLE_LIGHT, MAPLE_MID } from '../line-maple-glyph';
import { SUN_CY, SUN_HORIZON, SUN_LIGHT, SUN_R, SUN_RAY_LEN, SUN_TAB_HORIZON_D, SUN_TAB_TRANSFORM, sunRays } from '../line-sun-glyph';
import { MIAMI_PALM_CROWN, MIAMI_PALM_FIT, MIAMI_PALM_INK, MIAMI_PALM_TRUNK } from '../line-miami-palm-glyph';
import { SNOWFLAKE } from '../line-snowflake-glyph';
import { JELLYFISH, JELLY_ARMS, JELLY_BELL, JELLY_CREST, JELLY_TENTACLES, JELLY_VEINS_FINE, JELLY_VEINS_MAIN } from '../line-jellyfish-glyph';
import { SHELL, SHELL_BODY, SHELL_EARS, SHELL_RIBS } from '../line-shell-glyph';
import { LAMP, LAMP_GLASS, LAMP_IRON } from '../line-lamp-glyph';
import { YACHT, YACHT_HULL, YACHT_JIB, YACHT_MAIN, YACHT_MAST, YACHT_PC_JIBS } from '../line-yacht-glyph';
import { PUFFIN, PUFFIN_BILL, PUFFIN_BILL_GROOVE, PUFFIN_BILL_INNER, PUFFIN_BILL_OUTER, PUFFIN_BILL_TIP, PUFFIN_BODY, PUFFIN_EYE, PUFFIN_EYE_LINE, PUFFIN_FACE, PUFFIN_FEET, PUFFIN_FOOT, PUFFIN_WING } from '../line-puffin-glyph';
import { FLOWER, FLOWER_FOLIAGE, FLOWER_GREEN, FLOWER_HEAD, FLOWER_SHADE } from '../line-meadow-flower-glyph';
import { VIOLIN, VIOLIN_BRIDGE, VIOLIN_FHOLES, VIOLIN_POSE, VIOLIN_STRINGS } from '../line-violin-glyph';
import { CLAVEL, CLAVEL_FOLIAGE, CLAVEL_GREEN, CLAVEL_HEAD, CLAVEL_INNER } from '../line-clavel-glyph';
import { VOLCANO, VOLCANO_CONE, VOLCANO_FISSURE, VOLCANO_GLOW, VOLCANO_PLUME, VOLCANO_RIVER, VOLCANO_SHOULDER } from '../line-volcano-glyph';
import { FUJI_MOUNT, FUJI_SNOW } from '../line-pagoda-glyph';
import { mixHex } from '../line-electric-colors';
import {
  PARASOL_GREEN, PARASOL_PANELS_A, PARASOL_PANELS_B, PARASOL_PIVOT, PARASOL_POLE,
  PARASOL_SOLID, PARASOL_TAB_POSE, PARASOL_YELLOW,
} from '../line-parasol-glyph';
import {
  EIFFEL_BASE, EIFFEL_DECKS, EIFFEL_DEEP, EIFFEL_GOLD, EIFFEL_LAMP, EIFFEL_LAMPS,
  EIFFEL_LEDGE_SHADOW, EIFFEL_SHADE, EIFFEL_SOLID,
} from '../line-eiffel-glyph';
import {
  MAIDEN_BASE, MAIDEN_CAP, MAIDEN_COLLAR, MAIDEN_FLAG, MAIDEN_FLAG_RED, MAIDEN_GALLERY, MAIDEN_INK,
  MAIDEN_POLE, MAIDEN_ROOF, MAIDEN_ROOF_TILE, MAIDEN_SHAFT, MAIDEN_SOLID, MAIDEN_STONE, MAIDEN_WINDOWS,
} from '../line-maiden-glyph';
import { lineTabOff, lineTabOn } from '../line-tab-colors';

/* The glass-legibility helpers and BOTH tab-colour chains moved to
 * line-tab-colors.ts, so the exported asset and the phone mockup can no longer
 * disagree. Re-exported here because callers already import it from this file. */
export { glassSafeGlyph } from '../line-tab-colors';

/**
 * One LINE bottom-nav tab button (the i_03..i_38 slots), drawn live on the
 * /line workbench instead of a generated PNG. The glyph is a Phosphor "fill"
 * mask (assets/common/line-tab-icons, MIT) tinted via CSS mask, exactly like
 * the generator tints the same masks, so the live tile and the export are
 * pixel-twins.
 *
 * OFF = muted glyph (secondaryForeground) at 85% of the base size; ON = an
 * ELECTRIC glyph at 95%, colour-matched to the aurora menu-background ribbons:
 * home green / chats blue / calls pink. News-Today-Calls share one slot in the
 * app (user-swappable), so that family is pink; VOOM green and Wallet blue
 * keep neighbours distinct in the typical Home-Chats-VOOM-News-Wallet order.
 */
@Component( {
  selector: 'app-line-menu-button',
  templateUrl: './line-menu-button.component.html',
  styleUrls: [ './line-menu-button.component.scss' ],
} )
export class LineMenuButtonComponent implements OnChanges {

  @Input() product!: Product;

  /** Tab slot: home | chats | calls | news | today | voom | wallet | shopping | mini. */
  @Input() name = 'home';

  /** Active (ON) vs idle (OFF) state. */
  @Input() on = false;

  /**
   * iOS 26 (Liquid Glass) variant: LINE's new i_XX_g slots are landscape
   * 80x56 (the system draws the glass tab background, our PNG is only the
   * glyph, which must read on both black and white). Same glyphs + colours as
   * the classic set so the theme's icon language carries over; the glyph is
   * sized against the short (56px) edge instead of the tile width.
   */
  @Input() g = false;

  color = '#85A2B3';
  /** The ON glow colour. Normally the icon colour, but the custom Sakura glyphs
   *  glow in their OWN colour (green for the Chat leaf-bubble) so a green icon
   *  never gets a pink halo (owner 2026-07-29). */
  glowColor = '#85A2B3';
  maskUrl = '';
  /** Sakura's Home tab is a cherry-BLOSSOM glyph (owner 2026-07-29: a flower
   *  like the passcode dots, in sakura pink — replaces the earlier tree). ON =
   *  filled sakura-pink petals + golden stamen; OFF = single-tint silhouette. */
  isTree = false;
  /** Wide heart-lobed 5-petal blossom (shared with the passcode dots). */
  readonly blossomPetal = 'M0,-12 C-13,-17 -14,-41 -6,-46 Q-2.5,-42 0,-37 Q2.5,-42 6,-46 C14,-41 13,-17 0,-12 Z';
  readonly blossomRots = [ 0, 72, 144, 216, 288 ];
  /** Sakura's Chat tab: a light-green speech bubble with sakura-pink petals
   *  (owner 2026-07-29). Drifting-petal path shared with line-chat-bg. */
  isChat = false;
  readonly driftPetal = 'M0,0 C-2.2,-1.5 -2.0,-5.0 -0.4,-6.0 L0,-5.4 L0.4,-6.0 C2.0,-5.0 2.2,-1.5 0,0 Z';

  /** Tokyo's Home tab is a Tokyo TOWER glyph (owner 2026-08-05) — rendered by
   *  the shared <app-line-tower-glyph>, ON in the tower's red, OFF muted. */
  isTower = false;

  /** Everest's Home tab is the SUMMIT glyph (the 2026-08-06 restart) — the same
   *  hero motif as its passcode dots, so the theme reads as one language. ON =
   *  the peak filled in the sunrise orange with bright snowcaps; OFF = the plain
   *  silhouette in the muted icy tone. */
  isPeak = false;

  /** Aurora's Home tab is the ORB — the same porthole its passcode dots draw
   *  (owner 2026-08-12), so the theme reads as one language. ON = a blue-led
   *  aurora sky behind the gradient rim; OFF = the bare silhouette. */
  isOrb = false;

  /** Sequoia's Home tab is the REDWOOD — the same tree its passcode dots draw
   *  (line-tree-glyph). ON = the lit canopy over its trunk; OFF = the plain
   *  silhouette in the muted tab tone. */
  isSeq = false;

  /** Autumn's Home tab is the MAPLE — the same traced leaf its passcode dots and
   *  its wallpaper carry (line-maple-glyph). ON = the lit blade; OFF = the plain
   *  silhouette in the muted tab tone. */
  isMaple = false;

  /** Roraima's Home tab is the SETTING SUN — the same glyph its passcode dots
   *  draw (owner 2026-08-13: "home menu button batan güneş olacak passcode
   *  gibi"), so the theme reads as one language. It replaces the tepui, which
   *  keeps the menu strip and the wallpaper.
   *
   *  ON = the disc lit from a pale pink into the ON pink, rays and horizon in
   *  the ON pink; OFF = the same object flat in the muted tab tone. Everything
   *  below the horizon is clipped, which is what makes it read as SETTING rather
   *  than as a plain sun. */
  isSun = false;

  /**
   * Miami's Home tab is the PALM — the same tree its passcode dots draw, and the
   * foreground of the theme's own icon.
   *
   * TWO TONES when active (owner 2026-08-13), matching the passcode exactly: the
   * trunk holds the palette's dark pink and only the canopy carries the tab
   * colour — which is now the aqua, so the Home tab lights up exactly like a
   * passcode dot. This overrides the earlier note here that a second tone on the
   * trunk would read as a smudge at tab size: the split measures 4.05:1, which
   * survives the downscale to 43px comfortably.
   *
   * Idle stays ONE tone: an unlit tab is a silhouette, and that is the rule
   * every other theme's Home glyph follows.
   */
  isPalm = false;
  readonly palmCrown = MIAMI_PALM_CROWN;
  readonly palmTrunk = MIAMI_PALM_TRUNK;
  readonly palmTrunkInk = MIAMI_PALM_INK;
  /** Same computed fit the passcode uses, so the Home tab's tree and the lock
   *  screen's are one drawing at one size rather than two that drifted. */
  readonly palmFit = MIAMI_PALM_FIT;

  /**
   * Istanbul's Home tab is the KIZ KULESİ — the Maiden's Tower (owner
   * 2026-08-14), the city's one silhouette everybody can name and the thing
   * standing in the Bosphorus at the bottom-right of the theme's own icon. The
   * glyph is TRACED from that icon (line-maiden-glyph): the shaft, the balcony
   * collar and the low off-centre base are the icon's proportions, measured
   * column by column and rescaled into the shared 120 box, so the tab and the
   * store icon draw the same tower.
   *
   * ON = the lit tower in four tones, exactly the draw order the module
   * documents: base, shaft and gallery in sunlit stone, the wing's roof and its
   * cornice in Rooftop Red, the cap, collar and flagpole in Midnight Strait, the
   * flag in the brighter Deep Kilim, and the windows as ink at 0.72 so they read
   * as shadow rather than as holes.
   *
   * OFF = MAIDEN_SOLID, the whole tower as ONE path in ONE flat tab tone — an
   * unlit tab is a silhouette, the rule every other theme's Home glyph follows.
   * The path's subpaths are all wound the same way, so a nonzero fill unions the
   * overlaps into a single shape instead of punching holes in it.
   */
  /** Paris's Home tab is the EIFFEL TOWER — the same tower its passcode dots
   *  draw (owner 2026-08-14), so the two screens are one glyph.
   *
   *  ON = the tower lit: the body in Eiffel Gold, the legs and platform bands a
   *  step deeper where the floodlights stand, a Bordeaux line under each ledge
   *  and Champagne lamps along the top of it. OFF = the same contour flat in the
   *  tab's idle tone.
   *
   *  The ARCH under the legs is a real hole in both states and must stay one: at
   *  22px it is the only thing separating this from Tokyo's tower, whose decks
   *  are painted on rather than cut into the silhouette. */
  isEiffel = false;
  readonly eiffelSolid = EIFFEL_SOLID;
  readonly eiffelBase = EIFFEL_BASE;
  readonly eiffelDecks = EIFFEL_DECKS;
  readonly eiffelLamps = EIFFEL_LAMPS;
  readonly eiffelShadow = EIFFEL_LEDGE_SHADOW;
  readonly eiffelGold = EIFFEL_GOLD;
  readonly eiffelDeep = EIFFEL_DEEP;
  readonly eiffelLamp = EIFFEL_LAMP;
  readonly eiffelShade = EIFFEL_SHADE;

  /** Rio's Home tab is a PLAJ ŞEMSİYESİ — the same beach parasol its passcode
   *  dots draw (owner 2026-08-14: "şimdi home da şemsiye olacak"), so the tab bar
   *  and the lock screen are one glyph again, which is how every theme in this
   *  family but Istanbul works.
   *
   *  It REPLACES Cristo Redentor, which held this tab first. The statue is still
   *  in the tree (line-cristo-glyph) and it lost the same argument twice: a
   *  religious monument sets a register that a chat theme's furniture does not
   *  want, and once the passcode had a parasol the tab had a choice between
   *  matching it and being a second subject.
   *
   *  ON = the canopy in its two colours with the pole under it; OFF = the same
   *  silhouette flat in the tab's idle tone. */
  isParasol = false;
  readonly parasolSolid = PARASOL_SOLID;
  readonly parasolA = PARASOL_PANELS_A;
  readonly parasolB = PARASOL_PANELS_B;
  readonly parasolPole = PARASOL_POLE;
  readonly parasolGreen = PARASOL_GREEN;
  readonly parasolYellow = PARASOL_YELLOW;
  readonly parasolTransform =
    `translate(${ PARASOL_PIVOT[ 0 ] },${ PARASOL_PIVOT[ 1 ] }) rotate(${ PARASOL_TAB_POSE.a })`
    + ` scale(${ PARASOL_TAB_POSE.k }) translate(${ -PARASOL_PIVOT[ 0 ] },${ -PARASOL_PIVOT[ 1 ] })`;

  /** Tallinn's Home tab is a KAR TANESİ — the same snowflake its passcode dots
   *  and its chat wallpaper draw (owner 2026-08-14), so all three assets are one
   *  object.
   *
   *  One path in both states: a flake has no interior parts worth picking out at
   *  22px, so ON and OFF differ only in colour. */
  isSnowflake = false;
  readonly snowflake = SNOWFLAKE;

  /** Ocean's Home tab is a DENİZANASI — the same jellyfish its passcode dots and
   *  its chat wallpaper draw, so all three assets are one animal.
   *
   *  ON is THREE tones, which is one more than any other glyph in this family
   *  risks, and it survives the downscale because of where they sit: the bell in
   *  the tab colour, the strands a step DEEPER (they hang under the bell, and a
   *  drawing lit from above has its shadow there), and a pale crest INSIDE the
   *  dome. The crest never touches the silhouette, so at 22px it costs nothing if
   *  it washes out — the outline is unchanged either way. That is the test a
   *  third tone has to pass and the reason the strands do not carry one.
   *
   *  OFF = JELLYFISH, the whole animal as ONE path in ONE flat tone. An unlit tab
   *  is a silhouette, the rule every other theme's Home glyph follows. Every
   *  subpath is wound clockwise, so a nonzero fill unions the strands into the
   *  bell instead of punching them out of it. */
  isJelly = false;
  readonly jellyBell = JELLY_BELL;
  readonly jellyArms = JELLY_ARMS;
  readonly jellyTentacles = JELLY_TENTACLES;
  readonly jellyCrest = JELLY_CREST;
  readonly jellySolid = JELLYFISH;
  readonly jellyVeinsMain = JELLY_VEINS_MAIN;
  readonly jellyVeinsFine = JELLY_VEINS_FINE;
  /** The capillaries, owner 2026-08-15 ("elektrik mavisi pembesi"). Fixed hues,
   *  NOT derived from `color`: the whole point of them is that they are a
   *  different colour from the bell they sit in, and deriving them from the glyph
   *  tone would collapse them into it the moment that tone moves — which it did
   *  three times in one evening.
   *
   *  The first pass took them from the theme's own lagoon and rose (#3FB4D8 /
   *  #F29BB4) at 0.85 opacity, and the owner asked for them again, which is the
   *  answer: at tab size those were a texture, not a colour. ELECTRIC now, and
   *  drawn at full strength. */
  readonly jellyVeinBlue = '#39C6F0';
  readonly jellyVeinPink = '#FF7DAE';
  /** The strands, and the crest: both derived from the tab colour rather than
   *  pinned, so a colour change in line-tab-colors carries through the whole
   *  animal instead of leaving two hardcoded tones behind (the drift this family
   *  keeps paying for). */
  get jellyDeep(): string { return mixHex( this.color, '#062024', 0.42 ); }
  /** The strands — four tentacles and two oral arms — on the LIT Home tab, in a
   *  reddish ELECTRIC PINK rather than a deepened bell tone (owner 2026-08-15).
   *
   *  It is the same move the capillaries are: the animal stops being one colour
   *  at two exposures and becomes a green bell trailing lit vessels, which is
   *  what a medusa actually looks like lit from inside. Deeper than the pink
   *  cross-vessels so the two pinks are a family and not a duplicate — the bell's
   *  vessels are the paler pair, the strands hanging below them the heavier. */
  readonly jellyStrand = '#FF5C8A';
  get jellyCrestColor(): string { return mixHex( this.color, '#FFFFFF', 0.72 ); }

  /** Salda's Home tab is a DENİZ KABUĞU — the same scallop its passcode dots and
   *  its chat wallpaper draw, so all three assets are one object.
   *
   *  ON is TWO tones: the shell in the tab colour with its ribs a step deeper.
   *  The ribs are structure rather than decoration, which is why they can carry a
   *  second tone where Ocean's strands could not carry a third — they run the
   *  length of the shell, so even when they wash out at 22px the flutes they land
   *  on still describe the same shape.
   *
   *  OFF = SHELL, body and ears as ONE path in ONE flat tone. */
  isShell = false;
  readonly shellBody = SHELL_BODY;
  readonly shellEars = SHELL_EARS;
  readonly shellRibs = SHELL_RIBS;
  readonly shellSolid = SHELL;
  /** The shell's ribs — the radial grooves, and the thing that makes a scallop a
   *  scallop rather than a fan.
   *
   *  A WARM shadow, never a cold one: an early pass mixed toward #0A2E36 and put
   *  dark teal ink inside a warm object, which is half of why the whole glyph once
   *  read as a turquoise fan.
   *
   *  PALE, and it used to be DEEP. The old mix ran 62% toward the shell's own
   *  brown, which worked while the glyph body was a cream: the grooves measured
   *  1.92:1 on it. The owner's sampled skin moved the body to a warm sand
   *  (#A87F4E) and that mix landed at 1.15:1 on it — the flutes disappeared and
   *  the shell went back to reading flat (owner 2026-08-15: "ON halleri çizgili
   *  olmalı").
   *
   *  So the grooves are LIT rather than shadowed now, 55% of the way to a cream:
   *  2.4:1 on the sand body. It is also the direction that survives the body
   *  moving again — a lit groove on a mid body has room in both directions,
   *  a shadowed one on a mid body has almost none. */
  get shellRibColor(): string { return mixHex( this.color, '#FFF6E6', 0.55 ); }

  /** Prague's Home tab is a SOKAK LAMBASI — the same gas street lamp its passcode
   *  dots draw, and its chat wallpaper scatters.
   *
   *  IT REPLACED A GOTHIC SPIRE. The first pass drew a cathedral tower with a
   *  cross on it and the owner's call was that a cross-topped tower reads as a
   *  religious emblem rather than as a city. That is right: on a chat theme it is
   *  a flag. The drawing was fine and the subject was wrong, which is the harder
   *  kind of note to take and the more useful one.
   *
   *  ON is TWO tones: the ironwork in the tab colour and the four panes LIT in a
   *  pale gold. That is the whole ON/OFF story of this theme — the lamps coming on
   *  over the river — and it is the same idea the cathedral's lit windows carried,
   *  attached to an object nobody can mistake for a symbol.
   *
   *  OFF = LAMP, iron and glass as ONE flat silhouette: an unlit lamp, with its
   *  panes simply part of it. */
  isLamp = false;
  readonly lampIron = LAMP_IRON;
  readonly lampGlass = LAMP_GLASS;
  readonly lampSolid = LAMP;
  get lampLit(): string { return mixHex( this.color, '#FFF6DC', 0.72 ); }

  /** Fuji's Home tab is the MOUNTAIN — Fuji itself, the same one its passcode dots
   *  and its chat wallpaper draw.
   *
   *  IT REPLACED A PAGODA. The pagoda was chosen because this family already had
   *  two mountains (Everest's peak, Etna's cone) and a third would be a third
   *  triangle; the owner's call is that Fuji is not a third anything. It is THE
   *  mountain, and the snowcap running down its gullies in tongues is a silhouette
   *  nothing else in the set has. They are right — the argument against it was
   *  about the category, and this mountain is not in the category.
   *
   *  ON is TWO tones and they are the two the mountain has: the rock in the tab
   *  colour and the SNOW in near-white over it. That snow is the whole glyph at
   *  22px — a bare cone would be Etna.
   *
   *  OFF = FUJI_MOUNT alone, one flat silhouette with no cap: an unlit Fuji is the
   *  mountain before the light reaches the summit. */
  isFuji = false;
  readonly fujiMount = FUJI_MOUNT;
  readonly fujiSnow = FUJI_SNOW;
  get fujiSnowColor(): string { return mixHex( this.color, '#FFFFFF', 0.86 ); }
  /** The cap on the IDLE tab: the tab bar's own fill, so the snow is the ground
   *  showing through the mountain rather than a mark laid over it. */
  readonly fujiSnowCut = '#FFF0EA';

  /** Etna's Home tab is a YANARDAĞ — the same volcano its passcode dots draw. Its
   *  chat wallpaper drifts EMBERS off it.
   *
   *  It is the most layered ON state in this family — cone, lava, crater glow and
   *  a plume — and the only one where the layers are a SEQUENCE rather than a set
   *  of parts: rock, then what is coming out of it, then what is coming out of
   *  that. At 22px the plume and the glow both wash out and the two that survive
   *  are the ones that matter, the dark cone and the red running down it.
   *
   *  OFF = VOLCANO, cone and shoulder as ONE flat silhouette with nothing coming
   *  out of it, which for this subject is what unlit means. */
  isVolcano = false;
  readonly volcanoCone = VOLCANO_CONE;
  readonly volcanoShoulder = VOLCANO_SHOULDER;
  readonly volcanoFissure = VOLCANO_FISSURE;
  readonly volcanoRiver = VOLCANO_RIVER;
  readonly volcanoGlow = VOLCANO_GLOW;
  readonly volcanoPlume = VOLCANO_PLUME;
  readonly volcanoSolid = VOLCANO;
  get volcanoRock(): string { return mixHex( this.color, '#100B0B', 0.62 ); }
  get volcanoVent(): string { return mixHex( this.color, '#FFE0A8', 0.68 ); }
  /**
   * The plume's ash.
   *
   * It is NOT the mountain's own colour. Painted in `color` it came back as a fat
   * dun BLOB over the crater, because this theme's ramp runs through a mid brown.
   *
   * It is a MEASURED ink rather than a pretty one, because the plume is the only
   * part of this glyph that has to survive on both faces of the iOS-26 glass tab:
   * #8B7B72 sits at relative luminance 0.22, which clears 3.8:1 on the light pill
   * and 4.9:1 on the dark glass. A paler ash looked better on the phone's dark
   * skin and disappeared completely on the light one. The theme's own colour is
   * mixed in at an eighth, which is enough to warm it and not enough to move it
   * out of that window.
   */
  get volcanoAsh(): string { return mixHex( '#8B7B72', this.color, 0.12 ); }

  /** Madrid's Home tab is the CLAVEL — the carnation, the same flower its passcode
   *  dots and its wallpaper draw. ON = the head in the tab colour over foliage in
   *  the theme's fixed green; OFF = the whole plant as one flat silhouette. */
  isClavel = false;
  readonly clavelSolid = CLAVEL;
  readonly clavelHead = CLAVEL_HEAD;
  readonly clavelInner = CLAVEL_INNER;
  readonly clavelFoliage = CLAVEL_FOLIAGE;
  readonly clavelGreen = CLAVEL_GREEN;
  get clavelShade(): string { return mixHex( this.color, '#3A0F0C', 0.34 ); }

  /** Vienna's Home tab is a KEMAN — the same violin its passcode dots draw. Its
   *  chat wallpaper draws the F-HOLES, which is the most economical fragment in
   *  this run: two marks that name the object they were cut out of and belong to
   *  nothing else.
   *
   *  ON is THREE tones and every one of them is a real part: the instrument in the
   *  tab colour, the f-holes and bridge cut into it in the dark of the body's
   *  inside, and the strings a pale hairline over the whole thing. The f-holes are
   *  what carry it at 22px — they sit on the waist, so even washed out they mark
   *  exactly where the silhouette pinches.
   *
   *  OFF = VIOLIN, the whole instrument as ONE flat silhouette. */
  isViolin = false;
  readonly violinSolid = VIOLIN;
  readonly violinPose = VIOLIN_POSE;
  readonly violinFholes = VIOLIN_FHOLES;
  readonly violinBridge = VIOLIN_BRIDGE;
  readonly violinStrings = VIOLIN_STRINGS;
  get violinInside(): string { return mixHex( this.color, '#180A08', 0.62 ); }
  get violinString(): string { return mixHex( this.color, '#FFF6E2', 0.72 ); }

  /** Spring's Home tab is a ÇAN ÇİÇEĞİ — the same bellflower its passcode dots and
   *  its chat wallpaper draw.
   *
   *  IT REPLACED A HUMMINGBIRD, which the owner called careless and was. A bird in
   *  flight has four separate parts that all have to agree with each other, and at
   *  22px they never did; a hanging bell is ONE shape.
   *
   *  ON is THREE tones and each is a different organ: the bell in the tab colour,
   *  the stem and leaf in the theme's green, and the throat a deep note inside the
   *  mouth. The throat is the one that carries it small — it sits in the middle of
   *  the widest part of the flower rather than on an edge.
   *
   *  OFF = BELLFLOWER, bell, stem and leaf as ONE flat silhouette. */
  isFlower = false;
  readonly flowerShade = FLOWER_SHADE;
  readonly flowerSolid = FLOWER;
  readonly flowerFoliage = FLOWER_FOLIAGE;
  readonly flowerHead = FLOWER_HEAD;
  /** The stem and leaves keep a saturated forest green whatever the cup is
   *  (owner 2026-08-16). A bed of tulips is four bloom colours on one plant. */
  readonly flowerGreen = FLOWER_GREEN;
  /** The two OUTER petals, a shade deeper than the centre one. A tulip is not one
   *  flat colour — the side petals turn away from the light — and this is the whole
   *  of the modelling that stops the cup reading as a balloon. */
  get flowerShadeTone(): string { return mixHex( this.color, '#2A1038', 0.30 ); }

  /** Reynisfjara's Home tab is a PUFİN — the same bird its passcode dots and its
   *  chat wallpaper draw.
   *
   *  IT REPLACED THREE SEA STACKS. The rocks were redrawn once for carelessness and
   *  the redraw fixed the drawing without fixing the subject: a dark lump is not
   *  charming at any size, and it shares a silhouette family with half this set.
   *  The same beach's puffins are round, they have a face, and their bill is one of
   *  the few outlines in nature nothing else has.
   *
   *  ON is the most layered glyph in this run — nine paths — and every one of them
   *  is a material rather than a shade: the body in the tab colour, the face a warm
   *  off-white, the bill's two plates in the theme's own amber and rust, the feet
   *  in that same rust, the eye near-black. At 22px the wing and the groove wash
   *  out and the six that survive are exactly the six that identify the bird.
   *
   *  OFF = PUFFIN, feet, body and bill as ONE flat silhouette. */
  isPuffin = false;
  /** Reynisfjara's Home tab is the STUÐLABERG — the basalt column cluster its
   *  passcode dots and its chat wallpaper draw. It replaced a puffin, which the
   *  owner read as a penguin (2026-08-16); see line-basalt-glyph. */
  isBasalt = false;
  readonly basaltFaces = BASALT_FACES;
  readonly basaltFacesAlt = BASALT_FACES_ALT;
  readonly basaltTops = BASALT_TOPS;
  readonly basaltSolid = BASALT;
  /** The shaded half of the columns — the pair that separates faces which
   *  physically touch. */
  get basaltAltTone(): string { return mixHex( this.color, '#0E1216', 0.42 ); }
  /** The broken tops: a fresh basalt break is much paler than the weathered face
   *  under it, and at this size it is the only colour information the rock has. */
  get basaltTopTone(): string { return mixHex( this.color, '#FFFFFF', 0.46 ); }
  readonly puffinBody = PUFFIN_BODY;
  readonly puffinBill = PUFFIN_BILL;
  readonly puffinBillTip = PUFFIN_BILL_TIP;
  readonly puffinBillGroove = PUFFIN_BILL_GROOVE;
  readonly puffinFace = PUFFIN_FACE;
  readonly puffinEye = PUFFIN_EYE;
  readonly puffinEyeLine = PUFFIN_EYE_LINE;
  readonly puffinWing = PUFFIN_WING;
  readonly puffinFeet = PUFFIN_FEET;
  readonly puffinSolid = PUFFIN;
  /** The bill's two plates and the feet, off the theme's own warm tokens — see
   *  line-puffin-glyph. They do NOT vary with the tab colour: a puffin's bill is
   *  the same colour whether the tab is selected or not, and it is the one warm
   *  thing this theme owns. */
  readonly puffinBillOuter = PUFFIN_BILL_OUTER;
  readonly puffinBillInner = PUFFIN_BILL_INNER;
  readonly puffinFoot = PUFFIN_FOOT;
  /** The face disc. A puffin's is white; mixed most of the way to a warm off-white
   *  it stays pale enough to carry the eye and stops short of being the brightest
   *  thing on a muted Nordic tab bar. */
  get puffinPale(): string { return mixHex( this.color, '#F2EFE8', 0.82 ); }
  /** The eye, and the line running back from it. */
  get puffinInk(): string { return mixHex( this.color, '#14171B', 0.80 ); }
  /** The folded wing — a step off the body rather than a different colour. */
  get puffinWingTone(): string { return mixHex( this.color, '#20262C', 0.30 ); }

  /** Sydney's Home tab is a YELKENLİ — a harbour yacht, the same boat its passcode
   *  dots and its chat wallpaper draw.
   *
   *  IT REPLACED THE OPERA HOUSE. The building was drawn twice — pointed shells,
   *  then blunt ones after the owner sent the first back — and the second was a
   *  better drawing of the same wrong thing. A landmark is a postcard; a boat is
   *  doing something, which is the friendlier register for a chat theme. It also
   *  fixed the wallpaper, where one shell of that building read as a shark fin.
   *
   *  ON is THREE tones and each is a material: the mainsail in the tab colour, the
   *  jib a step lighter so the two sails separate without an outline, the hull and
   *  the rig a step deeper because the wet half of a boat is its darkest.
   *
   *  OFF = YACHT, hull, rig and both sails as ONE flat silhouette. */
  isYacht = false;
  readonly yachtHull = YACHT_HULL;
  readonly yachtMast = YACHT_MAST;
  readonly yachtMain = YACHT_MAIN;
  readonly yachtJib = YACHT_JIB;
  readonly yachtSolid = YACHT;
  /** The hull and the rig, a step DEEPER than the sails. Water-line down is the
   *  wet half of a boat and it is always the darkest part of one. */
  get yachtHullTone(): string { return mixHex( this.color, '#06384A', 0.46 ); }
  /** The jib, a step LIGHTER. The two sails have to separate somehow, and tone is
   *  the only way a flat fill can do it — the mast between them is two units wide
   *  and gone by 30px. */
  /** The RIGHT-hand sail on the lit Home tab, YELLOW (owner 2026-08-15) — the
   *  same three-colour boat the passcode dots draw: blue mainsail, yellow jib,
   *  deeper hull.
   *
   *  It takes the DEEPEST of the passcode's four jibs rather than a mid one. The
   *  tab glyph is a single state and it sits on a near-white bar, so a pale yellow
   *  there is a hole in the sail; the deep amber is the only member of that ramp
   *  that holds its own beside the mainsail's blue (3.89:1 on white against the
   *  #0E7BA8 mainsail's 4.75).
   *
   *  Fixed, not derived from `color`: the whole point is that it is a different
   *  colour from the mainsail, and a mix of the mainsail collapses into it the
   *  moment that tone moves. */
  get yachtJibTone(): string { return YACHT_PC_JIBS[ YACHT_PC_JIBS.length - 1 ]; }

  isMaiden = false;
  readonly maidenBase = MAIDEN_BASE;
  readonly maidenShaft = MAIDEN_SHAFT;
  readonly maidenGallery = MAIDEN_GALLERY;
  readonly maidenRoof = MAIDEN_ROOF;
  readonly maidenCollar = MAIDEN_COLLAR;
  readonly maidenCap = MAIDEN_CAP;
  readonly maidenPole = MAIDEN_POLE;
  readonly maidenFlag = MAIDEN_FLAG;
  readonly maidenWindows = MAIDEN_WINDOWS;
  readonly maidenSolid = MAIDEN_SOLID;
  readonly maidenStone = MAIDEN_STONE;
  readonly maidenInk = MAIDEN_INK;
  readonly maidenRoofTile = MAIDEN_ROOF_TILE;
  readonly maidenFlagRed = MAIDEN_FLAG_RED;
  readonly sunR = SUN_R;
  readonly sunCy = SUN_CY;
  readonly sunHorizonY = SUN_HORIZON;
  readonly sunHorizonD = SUN_TAB_HORIZON_D;
  readonly sunShift = SUN_TAB_TRANSFORM;
  readonly sunRaysD = sunRays( SUN_CY, SUN_RAY_LEN );
  readonly sunLight = SUN_LIGHT;
  readonly mapleBlade = MAPLE_BLADE;
  readonly mapleDeep = MAPLE_DEEP;
  readonly mapleMid = MAPLE_MID;
  readonly mapleLight = MAPLE_LIGHT;
  readonly treeCanopy = TREE_CANOPY;
  readonly treeTrunk = TREE_TRUNK;
  readonly treeDark = TREE_DARK;
  readonly treeMid = TREE_MID;
  readonly treeLight = TREE_LIGHT;
  readonly treeBark = TREE_BARK;
  readonly orbR = ORB_R;
  readonly orbRimStops = ORB_RIM_STOPS;
  readonly orbRimOff = ORB_RIM_OFF;
  readonly orbStars = ORB_STARS[ 0 ];
  readonly orbBands = ORB_TAB_SET.map( b => ( {
    d: ORB_BANDS[ b.band ], fill: ORB_COLORS[ b.color ], o: b.o,
  } ) );
  /** OFF draws the aurora POSITIVELY instead of carving it: a filled silhouette
   *  with a hole in it read as a smudge, so the unlit tab is now the ring plus
   *  all three bands in the muted tone, over an EMPTY sky (owner 2026-08-12). */
  readonly orbOffBands = ORB_TAB_SET.map( b => ORB_BANDS[ b.band ] );
  /**
   * The orb has to end up the SAME SIZE as its neighbours (owner 2026-08-12).
   * The Phosphor masks do NOT fill their 120 box — the art measures ~53% of the
   * tile — so the fit factor is measured against a rendered neighbour rather
   * than derived from the box: at 1.228 the ring's outer edge lands on the same
   * width the chats glyph draws, in both states (both carry the same 5px ring,
   * so one factor covers ON and OFF).
   */
  readonly orbFit = 'translate(60,60) scale(1.228) translate(-60,-60)';
  /** Summit silhouette + snowcaps, in the passcode's 120 box — literally the same
   *  paths the passcode dots draw, so the two can never drift (line-peak-glyph). */
  readonly peakBody = PEAK_BODY;
  readonly peakCap = PEAK_CAP;
  readonly peakCapSmall = PEAK_CAP_SMALL;
  /** Glyph width as % of the 128px tile: base 96/128 = 75%, OFF x0.85,
   *  ON x0.95 grown 5% twice (x1.05 x1.05 ~= 78.7%). */
  sizePct = 63.75;

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    this.maskUrl = `url('/assets/common/line-tab-icons/${ this.name }.png')`;
    this.isTree = p.key === 'sakura' && this.name === 'home';
    this.isChat = p.key === 'sakura' && this.name === 'chats';
    this.isTower = p.key === 'tokyo' && this.name === 'home';
    this.isPeak = p.key === 'everest' && this.name === 'home';
    this.isOrb = p.key === 'aurora-borealis' && this.name === 'home';
    this.isSeq = p.key === 'sequoia' && this.name === 'home';
    this.isMaple = p.key === 'autumn' && this.name === 'home';
    this.isSun = p.key === 'roraima' && this.name === 'home';
    this.isPalm = p.key === 'miami' && this.name === 'home';
    this.isMaiden = p.key === 'istanbul' && this.name === 'home';
    this.isEiffel = p.key === 'paris' && this.name === 'home';
    this.isParasol = p.key === 'rio-de-janeiro' && this.name === 'home';
    this.isSnowflake = p.key === 'tallinn' && this.name === 'home';
    this.isJelly = p.key === 'ocean' && this.name === 'home';
    this.isShell = p.key === 'salda' && this.name === 'home';
    this.isLamp = p.key === 'prague' && this.name === 'home';
    this.isYacht = p.key === 'sydney' && this.name === 'home';
    this.isBasalt = p.key === 'reynisfjara' && this.name === 'home';
    this.isFlower = p.key === 'spring' && this.name === 'home';
    this.isViolin = p.key === 'vienna' && this.name === 'home';
    this.isClavel = p.key === 'madrid' && this.name === 'home';
    this.isVolcano = p.key === 'etna' && this.name === 'home';
    this.isFuji = p.key === 'fuji' && this.name === 'home';
    // g (80x56): the square glyph fits the 56px height — ON ~46px (57.5% of the
    // 80px width), OFF ~38px (48%) — same ON/OFF growth ratio as the classic set.
    this.sizePct = this.g
      ? ( this.on ? 57.5 : 48 )
      : ( this.on ? 75 * 0.95 * 1.05 * 1.05 : 75 * 0.85 );
    // The tower silhouette occupies less of its viewBox than the Phosphor
    // glyphs, so scale it up to fill a comparable area.
    if ( this.isTower ) { this.sizePct *= 1.14; }
    // The summit is wide but short, so it reads smaller than the square Phosphor
    // glyphs at the same width; give it a little back.
    if ( this.isPeak ) { this.sizePct *= 1.08; }
    // No size adjustment for the orb: it must match the other tabs exactly
    // (owner 2026-08-12 — shrunk by 8% it read visibly smaller in the row).
    // The redwood is TALL and narrow, so at the same width it measured 52px
    // against the chats glyph's 68; scale it to match.
    if ( this.isSeq ) { this.sizePct *= 1.31; }
    // The Home glyph must MEASURE with its neighbours — a standing rule the owner
    // has raised on two themes (Aurora's orb 2026-08-12, this one 2026-08-13).
    // The Phosphor masks do NOT fill their 120 box, so the factor is measured
    // against a rendered neighbour rather than derived.
    //
    // The measurement that matters here is the DISC, not the glyph's bounding
    // box: the horizon line under it is far wider than the sun and was both
    // dominating the eye and dominating the bbox, so a "matched" Home button
    // still read small (owner: "alttaki çizgiden dolayı hissiyat yanıltıyor").
    // That line is now cut back to the rays' span (SUN_TAB_HORIZON_D).
    //
    // This only sets the ELEMENT's width; the drawing's own size comes from
    // SUN_TAB_ART_SCALE inside the viewBox, because a flex item wider than its
    // tile is silently shrunk back (see that constant).
    //
    // ONE factor covers both slot types. While the classic tile was hitting that
    // flex ceiling and the _g tile was not, the two needed different numbers to
    // land in the same place; with the drawing sized inside the box neither is
    // clamped and they behave identically. Kept under 100% so the ceiling stays
    // out of reach. The result is the _g proportion the owner signed off
    // (2026-08-13: "ios 26 home iyi görünüyor onun gibi yap") on both.
    if ( this.isSun ) { this.sizePct *= 1.06; }
    // The palm is TALL and narrow, so at the shared width it measures short next
    // to the square Phosphor glyphs; measured against a rendered neighbour, not
    // guessed (see [[line-home-glyph-equal-size]]).
    if ( this.isPalm ) { this.sizePct *= 1.12; }
    // The tower is a TALL thing and the module leaves air either side of its
    // base on purpose, so its ink fills only 87.5 of the 120 box across (73%)
    // where a Phosphor mask fills 81%. At the shared width it therefore draws
    // narrower than its neighbours, and the standing rule is that the Home glyph
    // MEASURES with them, matched on WIDTH against a RENDERED neighbour rather
    // than derived from the box ([[line-home-glyph-equal-size]]).
    //
    // Measured at native px, istanbul's Home against the chats tab beside it:
    // classic OFF 60px of ink against 68, iOS-26 OFF 28px against 32 — a factor
    // of 1.133 and 1.143. Geometry says 0.8125 / 0.72917 = 1.114 and the extra
    // is the antialiased edge the mask carries; 1.14 lands OFF on 68 and 32, the
    // neighbour's own numbers, in both slot types. ONE factor covers both,
    // because nothing here is clamped: the widest state this produces is the
    // classic ON at 89.6% of the tile, under the 100% flex ceiling that silently
    // shrinks a .glyph-svg back (see SUN_TAB_ART_SCALE, where hitting it is what
    // made a growing sizePct change nothing at all).
    //
    // The ON pair measures ~2px wider than the neighbour's, which is the glow:
    // drop-shadow spreads a lit glyph's alpha, so the clean OFF pair is the
    // anchor and the lit art underneath lands on the same width.
    if ( this.isMaiden ) { this.sizePct *= 1.14; }
    // The jellyfish is TALL — 101 of the 120 box down, against the 82 it spans
    // across — so at the shared WIDTH it already out-measures its neighbours
    // vertically and needs pulling IN, not out. This is the first Home glyph in
    // the family that goes the other way, and it is the same rule underneath:
    // the glyph MEASURES with the row, matched on the neighbour's own rendered
    // ink rather than on this box ([[line-home-glyph-equal-size]]).
    //
    // Measured at native px against the chats tab beside it: at the shared width
    // the animal's bell alone drew 68px against the neighbour's 68 and the
    // strands then added another half-tile below, so the tab read as the tallest
    // thing in the row. At 0.88 the bell comes back to ~60 and the whole animal
    // occupies the neighbour's own square.
    if ( this.isJelly ) { this.sizePct *= 0.88; }
    // The shell is the jellyfish's opposite — 94 of the box across against 75
    // down — so at the shared WIDTH it draws SHORT next to the square Phosphor
    // masks and needs pulling in rather than out. Measured against the chats tab
    // beside it at native px: at the shared width the shell filled 68px across to
    // the neighbour's 68 but only 54 down to its 68, and the row read as having a
    // gap in it. At 0.92 the shell's own area matches the neighbour's ink, which
    // is what the standing rule actually asks for ([[line-home-glyph-equal-size]])
    // — matched on the drawing's weight, not on one dimension of its box.
    // ...and it applies to the SELECTED state only, in the other direction.
    //
    // The 0.92 above was measured against the idle Phosphor masks and it is now
    // gone. It went to the plain shared size first, on the owner's word that the
    // idle shell should match its neighbours, and then up again on their next look
    // — which is the honest answer to the standing rule here. A shell is 94 across
    // by 72 down; a Phosphor mask is square. Matched on WIDTH the shell reads
    // small in both states, so both states carry a boost, the selected one more.
    if ( this.isShell ) { this.sizePct *= this.on ? 1.10 : 1.08; }
    // The spire is the TALLEST glyph in this family — 104 of the box down against
    // 60 across — so at the shared width it towers over the row. Measured against
    // the chats tab at native px, the same way every factor here is: at 0.80 the
    // tower's own height comes back to the neighbour's 68px and the nave block
    // sits on the same baseline as the Phosphor masks' feet.
    // The lamp WAS the narrowest-and-tallest pair in the family after the violin —
    // 30 across against 106 down — and 0.82 was measured against the chats tab at
    // native px to bring that spike back to the row. The drawing is 40 by 102 now
    // (owner 2026-08-15: it read as too thin and long), so the correction it
    // needed shrank with it: at 0.82 the stockier lamp came back UNDER its
    // neighbours rather than level with them. It went to 0.90 and then past 1.0
    // (owner 2026-08-15, both states) — a 40-by-102 object matched on WIDTH to a
    // square mask still reads small, which is the same correction the shell needed
    // and for the same reason.
    if ( this.isLamp ) { this.sizePct *= 1.04; }
    // The opera house is WIDE and short — 108 of the box across against 88 down —
    // so like Salda's shell it draws short at the shared width and the row reads
    // as having a gap in it. Measured against the chats tab at native px: 0.96
    // puts the building's own area on the neighbour's ink.
    // The boat is WIDE — 94 across against 88 down — so it is the one glyph in
    // this set that wants holding back rather than pushing out. Measured against
    // the chats tab at native px: 0.92.
    // UP, not down. This was 0.92, measured to bring a wide-and-short drawing level
    // with the square Phosphor masks beside it — and level is the wrong target.
    // The Home tab is the theme's own object and the selected tab; it should read
    // as the biggest thing in the row (owner 2026-08-15: "orası focus, küçücük
    // olamaz"). See [[line-home-tab-must-be-big]].
    if ( this.isYacht ) { this.sizePct *= 1.12; }
    // The stacks are WIDE — 104 of the box across against 100 down, and most of
    // that height is one narrow spire, so the drawing's WEIGHT sits low and left.
    // Measured against the chats tab at native px: 0.94 puts the group's ink on
    // the neighbour's.
    // The bird is TALL — 100 down against 82 across — so it wants pulling in where
    // the rocks it replaced wanted pushing out. Measured against the chats tab at
    // native px: 0.88.
    // The cluster is WIDE and short — 80 across by 86 down — so at the shared
    // width it reads smaller than a square mask, and the Home tab wants to be the
    // biggest thing in the row anyway ([[line-home-tab-must-be-big]]).
    if ( this.isBasalt ) { this.sizePct *= 1.10; }
    // The bird is WIDE and shallow — it spans 100 of the box across and only 62
    // down, because a long beak on one end and a fanned tail on the other is what
    // a hovering hummingbird IS. Measured against the chats tab at native px:
    // at the shared width it drew 68 across to 42 down against the neighbour's
    // 68 square, so it is grown rather than shrunk.
    // The bellflower is TALL and narrow — 95 down against 80 across — so unlike
    // the bird it went after, it needs no growing. Measured against the chats tab
    // at native px: 0.92.
    // UP, not down. 0.92 brought the bell level with the square Phosphor masks,
    // and level is the wrong target for the Home tab ([[line-home-tab-must-be-big]]).
    if ( this.isFlower ) { this.sizePct *= 1.10; }
    // The violin is the NARROWEST glyph in this family, and it is also the tallest
    // — measured in the shipped tile it came out 53px wide by 111 tall where the
    // chats bubble beside it is 82 by 82. Both halves of that are wrong, and they
    // pull opposite ways: it needs to be wider, and it had no room to grow because
    // it was already touching the tile top and bottom.
    //
    // UPRIGHT (owner 2026-08-16) and grown as far as the tile allows. A standing
    // violin is 58 units across by 87 down, so width and height do not run out
    // together: the height is what binds, and this factor is set by it.
    //
    // What made the factor possible at all is HEAD_DROP — the neck was ten units
    // longer and the glyph was touching the top of the tile at two thirds of this
    // size. It has never once been too big; it has been too small three times.
    //
    // 1.82 is the CEILING, not a preference: at this factor the violin measures
    // about 125px tall in a band that stops at 130, so it is as large as an upright
    // instrument can be here. That still leaves it narrower than the chats bubble,
    // because 58-by-87 cannot be made to fill a square without being stretched, and
    // stretching a violin is what turns it into a chess pawn.
    if ( this.isViolin ) { this.sizePct *= 1.82; }
    // The carnation is 68 across by 100 down, so like every plant in this family
    // it is height-bound and the factor works on the width it has left. Measured
    // against the chats tab at native px.
    if ( this.isClavel ) { this.sizePct *= 1.10; }
    // The volcano fills its box almost exactly — 110 across, 102 down with the
    // plume — so it needs the least correction of anything here. Measured against
    // the chats tab at native px: 0.98.
    if ( this.isVolcano ) { this.sizePct *= 0.98; }
    // The pagoda is TALL and narrow — 84 across against 117 down — so like
    // Prague's spire it towers at the shared width. Measured against the chats tab
    // at native px: 0.84.
    // The mountain is WIDE and shallow — 108 across against 54 down — so unlike the
    // pagoda it replaced it draws short at the shared width, and a shallow glyph is
    // the case this family most often under-sizes.
    //
    // Raised from 1.06 (owner 2026-08-17). It is the same correction Salda's shell,
    // Prague's lamp, Sydney's yacht and Vienna's violin all needed, which is now
    // five times: the Home slot is the theme's own object and the selected tab, so
    // it should read as the largest thing in the row rather than as one of five
    // equals. Every one of those five was too small and none has ever been too big.
    if ( this.isFuji ) { this.sizePct *= 1.24; }
    if ( this.on ) {
      this.color = lineTabOn( p, this.name );
    } else {
      this.color = lineTabOff( p );
    }
  }

}
